import { useState } from 'react';
import { FiAlertTriangle, FiArrowUpCircle, FiX } from 'react-icons/fi';
import { usePreferences } from '../../contexts/PreferencesContext';
import { safeSessionStorage } from '../../utils/safeStorage';
import { UPGRADE_WHATSAPP_URL, chaveBarraFechada, percentualUsado, varianteDaBarra } from '../../utils/planoGratuito';
import { useTransactionUsage } from './useTransactionUsage';
import styles from './FreePlanBanner.module.css';

/**
 * Barra do plano Gratuito (MAN-364), logo abaixo do Header de todas as telas.
 *
 * - Com saldo: "Você está no plano Gratuito com N lançamentos" e a contagem.
 * - No limite: vira a barra vermelha "Você atingiu o limite de lançamentos!".
 *
 * O X esconde a barra até o navegador recarregar (sessionStorage), cada variante
 * por conta própria.
 */
export function FreePlanBanner() {
    const uso = useTransactionUsage();
    const { t } = usePreferences();
    const [fechadas, setFechadas] = useState<Record<string, boolean>>({});

    if (!uso?.freePlan || !uso.limit) return null;

    const variante = varianteDaBarra(uso.reached);
    const chave = chaveBarraFechada(variante);
    if (fechadas[chave] || safeSessionStorage.getItem(chave) === '1') return null;

    const fechar = () => {
        safeSessionStorage.setItem(chave, '1');
        setFechadas((atual) => ({ ...atual, [chave]: true }));
    };

    const limite = String(uso.limit);
    const atingiu = variante === 'limite';

    return (
        <div className={styles.linha}>
        <div className={`${styles.barra} ${atingiu ? styles.barraLimite : styles.barraContagem}`} role="status">
            <div className={`${styles.icone} ${atingiu ? styles.iconeLimite : styles.iconeContagem}`}>
                <FiAlertTriangle size={18} />
            </div>

            <div className={styles.texto}>
                {atingiu ? (
                    <>
                        <strong className={styles.titulo}>{t('planoGratuito.limiteTitulo')}</strong>
                        <span className={styles.descricao}>
                            {t('planoGratuito.limitePrefixo')}{' '}
                            <strong>{t('planoGratuito.limiteDestaque', { limite })}</strong>
                            {t('planoGratuito.limiteSufixo')}
                        </span>
                    </>
                ) : (
                    <>
                        <strong className={styles.titulo}>{t('planoGratuito.contagemTitulo', { limite })}</strong>
                        <span className={styles.contagem}>
                            <span className={styles.trilho} aria-hidden="true">
                                <span className={styles.progresso} style={{ width: `${percentualUsado(uso.used, uso.limit)}%` }} />
                            </span>
                            {t('planoGratuito.contagem', { usados: String(Math.min(uso.used, uso.limit)), limite })}
                        </span>
                    </>
                )}
            </div>

            <a className={styles.upgrade} href={UPGRADE_WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
                <FiArrowUpCircle size={16} />
                {t('planoGratuito.fazerUpgrade')}
            </a>

            <button type="button" className={styles.fechar} onClick={fechar} aria-label={t('planoGratuito.fechar')}>
                <FiX size={16} />
            </button>
        </div>
        </div>
    );
}
